import { Product } from '@/components/Product/ProductCard';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { useMemo } from 'react';

interface ImageInfo {
  src: string;
  type?: EProductImageType;
  isGray?: boolean;
}

export type ProductImagePriority = 'gray-first' | 'white-first';
export type HoverProductImagePriority = ProductImagePriority | 'white-second-gray-first';

interface UseProductImagesOptions {
  productData: Product;
  imageRule?: EProductImageType[];
  imageHoverRule?: EProductImageType[];
  productImagePriority?: ProductImagePriority;
  hoverProductImagePriority?: HoverProductImagePriority;
}

export const useProductImages = ({
  productData,
  imageRule = [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  imageHoverRule = [
    EProductImageType.MATERIAL,
    EProductImageType.CELEBRITY,
    EProductImageType.PRODUCT,
  ],
  productImagePriority = 'gray-first',
  hoverProductImagePriority = productImagePriority,
}: UseProductImagesOptions) => {
  // 🎯 最简洁方案：使用 useMemo 直接计算并返回结果，无需状态管理
  return useMemo(() => {
    const getProductImage = (): ImageInfo => {
      try {
        const {
          product = [],
          product_gray = [],
          celebrity = [],
          material = [],
        } = productData?.image || {};

        // 非镜框商品，只显示主图
        // 2025/9/19: 镜框、非镜框逻辑保持一致
        // if (productData.is_glass !== 1 && product.length > 0) {
        //   return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
        // }
        // 确保 imageRule 是数组
        if (!Array.isArray(imageRule) || imageRule.length === 0) {
          return { src: '' };
        }

        for (const type of imageRule) {
          if (type === EProductImageType.PRODUCT) {
            if (productImagePriority === 'white-first' && product.length > 0) {
              return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
            } else if (product_gray.length > 0) {
              return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
            } else if (product.length > 0) {
              return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
            }
          } else if (type === EProductImageType.CELEBRITY && celebrity.length > 0) {
            return { src: celebrity[0], type: EProductImageType.CELEBRITY, isGray: false };
          } else if (type === EProductImageType.MATERIAL && material.length > 0) {
            return { src: material[0], type: EProductImageType.MATERIAL, isGray: false };
          }
        }
        return { src: '' };
      } catch (error) {
        logger.error(error);
        return { src: '' };
      }
    };

    const getHoverImage = (productImg: ImageInfo): ImageInfo => {
      try {
        const {
          product = [],
          product_gray = [],
          celebrity = [],
          material = [],
        } = productData?.image || {};

        if (!Array.isArray(imageHoverRule) || imageHoverRule.length === 0) {
          return { src: '' };
        }

        for (const type of imageHoverRule) {
          if (type === EProductImageType.PRODUCT) {
            if (hoverProductImagePriority === 'white-second-gray-first') {
              if (product.length > 1) {
                return { src: product[1], type: EProductImageType.PRODUCT, isGray: false };
              }
              if (product_gray.length > 0) {
                return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
              }
              continue;
            }

            if (productImg.type === EProductImageType.PRODUCT) {
              if (hoverProductImagePriority === 'white-first' && product.length > 1) {
                return { src: product[1], type: EProductImageType.PRODUCT, isGray: false };
              } else if (hoverProductImagePriority === 'white-first' && product.length > 0) {
                return { src: '', type: EProductImageType.PRODUCT, isGray: false };
              } else if (product_gray.length > 1) {
                return { src: product_gray[1], type: EProductImageType.PRODUCT, isGray: true };
              } else if (product_gray.length > 0) {
                return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
              } else if (product.length > 1) {
                return { src: product[1], type: EProductImageType.PRODUCT, isGray: false };
              } else if (product.length > 0) {
                return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
              }
            } else if (hoverProductImagePriority === 'white-first' && product.length > 0) {
              return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
            } else if (product_gray.length > 0) {
              return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
            } else if (product.length > 0) {
              return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
            }
          } else if (type === EProductImageType.CELEBRITY && celebrity.length > 0) {
            return { src: celebrity[0], type: EProductImageType.CELEBRITY, isGray: false };
          } else if (type === EProductImageType.MATERIAL && material.length > 0) {
            return { src: material[0], type: EProductImageType.MATERIAL, isGray: false };
          }
        }
        return { src: '' };
      } catch (error) {
        logger.error(error);
        return { src: '' };
      }
    };

    const productImage = getProductImage();
    const hoverImage = getHoverImage(productImage);

    return {
      productImage,
      hoverImage,
      // 便捷的字符串访问
      productImageSrc: productImage.src,
      hoverImageSrc: hoverImage.src,
      // 刷新方法（实际上不需要，因为是纯计算）
      refreshImages: () => {
        // 纯计算函数，组件重新渲染时会自动"刷新"
      },
    };
  }, [
    // 🔧 关键修复：只依赖真正会影响结果的值
    productData?.id,
    productData?.image?.product,
    productData?.image?.product_gray,
    productData?.image?.celebrity,
    productData?.image?.material,
    productImagePriority,
    hoverProductImagePriority,
    // productData?.is_glass,
    // 确保 rules 是数组时才作为依赖
    Array.isArray(imageRule) ? imageRule : [],
    Array.isArray(imageHoverRule) ? imageHoverRule : [],
  ]);
};
